import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { formatDateOrdinal, formatDateTimeOrdinal } from './date_util';

export const ClientRequestDetail = () => {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shiftsPage, setShiftsPage] = useState(1);
  const SHIFTS_PER_PAGE = 50;

  useEffect(() => {
    fetch(`/apiclientRequest/${id}`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        setRequest(data);
        setLoading(false);

        // Fetch related shifts
        if (data && data[3]) {
          fetch(`/apiclientRequestShifts/${data[3].value}`, { credentials: 'include' })
            .then(res => res.ok ? res.json() : [])
            .then(shiftsData => setShifts(shiftsData || []));
        }
      })
      .catch(() => setLoading(false));
  }, [id]);

  const formatDate = (val) => val
    ? formatDateOrdinal(val)
    : '';

  if (loading) return <div className="location"><p>Loading...</p></div>;
  if (!request) return (
    <div className="location">
      <div className="locationDetailsWrapper">
        <p>Request not found.</p>
        <button onClick={() => navigate('/clientRequests')} className="mainButton">← Back to Guard Requests</button>
      </div>
    </div>
  );

  // Extract field values
  const locationCode = request[282] ? request[282].value : '';
  const dateOfRequest = request[6] ? formatDate(request[6].value) : '';
  const status = request[878] ? request[878].value : '';
  const address = request[284] ? request[284].value : '';
  const firstShiftStart = request[323] ? formatDate(request[323].value) : '';
  const lastShiftStart = request[324] ? formatDate(request[324].value) : '';
  const rate = request[938] ? request[938].value : null;
  const hourlyRate = (!rate || isNaN(parseFloat(rate)) || parseFloat(rate) === 0) ? 'pending' : `$${parseFloat(rate).toFixed(2)}`;
  const numberOfGuards = request[200] ? request[200].value : '';
  const guardType = request[89] ? request[89].value : '';
  const armedUnarmed = request[86] ? request[86].value : '';
  const dressCode = request[88] ? request[88].value : '';
  const insideOutside = request[87] ? request[87].value : '';
  const specialRequirements = request[90] ? request[90].value : '';
  const reason = request[133] ? request[133].value : '';

  // Status color logic (matches table view)
  let statusColor = "Gray";
  switch(status) {
    case "Complete":
    case "Confirmed":
        statusColor = "limeGreen";
    case "Scheduled":
      statusColor = "#B7966A";
      break;
    case "Pending Quote":
    case "Unscheduled":
      statusColor = "Orchid";
      break;
    case "Quoted":
    case "Unconfirmed":
      statusColor = "Gold";
      break;
    case "Cancelled":
    case "Expired":
    case "Declined":
      statusColor = "LightGray";
      break;
    default:
      statusColor = "Gray";
  }

  return (
    <div className="location">
      <div className="locationDetailsWrapper">
        <button onClick={() => navigate('/clientRequests')} className="mainButton" style={{ marginBottom: '16px' }}>← Back to Guard Requests</button>
        <h2>Security Guard Request Details</h2>

        {status && (
          <div style={{ display: 'inline-block', padding: '8px 16px', borderRadius: '20px', background: statusColor, color: 'white', fontWeight: 'bold', fontSize: '14px', marginBottom: '12px' }}>
            {status}
          </div>
        )}

        <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">Location Code</p><p className="field">{locationCode}</p>
          </div>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">Date of Request</p><p className="field">{dateOfRequest}</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">Address</p><p className="field">{address}</p>
          </div>
        </div>

        <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>Shift Information</h3>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">First Shift Start</p><p className="field">{firstShiftStart}</p>
          </div>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">Last Shift Start</p><p className="field">{lastShiftStart}</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">Number of Guards</p><p className="field">{numberOfGuards}</p>
          </div>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">Hourly Rate</p><p className="field">{hourlyRate}</p>
          </div>
        </div>

        <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>Guard Requirements</h3>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">Guard Type</p><p className="field">{guardType}</p>
          </div>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">Armed/Unarmed</p><p className="field">{armedUnarmed}</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">Dress Code</p><p className="field">{dressCode}</p>
          </div>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">Inside/Outside</p><p className="field">{insideOutside}</p>
          </div>
        </div>

        {specialRequirements && (
          <div style={{ marginBottom: '8px' }}>
            <p className="fieldLabel">Special Requirements</p><p className="field">{specialRequirements}</p>
          </div>
        )}

        {reason && (
          <div style={{ marginBottom: '8px' }}>
            <p className="fieldLabel">Reason</p><p className="field">{reason}</p>
          </div>
        )}

        {shifts && shifts.length > 0 && (() => {
          const sortedShifts = [...shifts].sort((a, b) => {
            const aStart = a && a[6] && a[6].value ? new Date(a[6].value).getTime() : Infinity;
            const bStart = b && b[6] && b[6].value ? new Date(b[6].value).getTime() : Infinity;
            return aStart - bStart;
          });
          const totalShiftsPages = Math.ceil(sortedShifts.length / SHIFTS_PER_PAGE);
          const shiftsStart = (shiftsPage - 1) * SHIFTS_PER_PAGE;
          const paginatedShifts = sortedShifts.slice(shiftsStart, shiftsStart + SHIFTS_PER_PAGE);
          const formatTime = formatDateTimeOrdinal;

          const getPageNumbers = (current, total) => {
            if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
            const pages = [1];
            if (current > 3) pages.push('...');
            for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
            if (current < total - 2) pages.push('...');
            pages.push(total);
            return pages;
          };

          return (
            <>
              <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>Related Shifts ({shifts.length})</h3>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>
                Showing {shiftsStart + 1}-{Math.min(shiftsStart + SHIFTS_PER_PAGE, shifts.length)} of {shifts.length} shifts
              </p>
              <div className="tableScroll">
              <table id="tableWrapper">
                <tbody>
                  <tr>
                    <th>Vendor Name</th>
                    <th>Location Code</th>
                    <th className="addressCell">Address</th>
                    <th>Armed/Unarmed</th>
                    <th>Guard Type</th>
                    <th>Temporary/Recurring</th>
                    <th>Shift Start (local time)</th>
                    <th>Shift End (local time)</th>
                  </tr>
                  {paginatedShifts.map((shift, idx) => (
                    <tr key={idx} onClick={() => navigate(`/shift/${shift[3]?.value}`, { state: { fromRequest: id } })} className="tableRow">
                      <td className="tableCell">{shift[378]?.value || ""}</td>
                      <td className="tableCell">{shift[321]?.value || ""}</td>
                      <td className="tableCell addressCell">{shift[898]?.value || ""}</td>
                      <td className="tableCell">{shift[107]?.value || ""}</td>
                      <td className="tableCell">{shift[108]?.value || ""}</td>
                      <td className="tableCell">{shift[1295]?.value || ""}</td>
                      <td className="tableCell">{shift[6] ? formatTime(shift[6].value) : ""}</td>
                      <td className="tableCell">{shift[7] ? formatTime(shift[7].value) : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {totalShiftsPages > 1 && (
                <div className="pagination-wrapper">
                  <button
                    className={`pagination-btn ${shiftsPage === 1 ? 'pagination-btn--disabled' : ''}`}
                    onClick={() => setShiftsPage(p => Math.max(1, p - 1))}
                    disabled={shiftsPage === 1}
                  >
                    Prev
                  </button>
                  {getPageNumbers(shiftsPage, totalShiftsPages).map((page, idx) => (
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} className="pagination-btn pagination-btn--disabled">...</span>
                    ) : (
                      <button
                        key={page}
                        className={`pagination-btn ${shiftsPage === page ? 'pagination-btn--active' : ''}`}
                        onClick={() => setShiftsPage(page)}
                      >
                        {page}
                      </button>
                    )
                  ))}
                  <button
                    className={`pagination-btn ${shiftsPage === totalShiftsPages ? 'pagination-btn--disabled' : ''}`}
                    onClick={() => setShiftsPage(p => Math.min(totalShiftsPages, p + 1))}
                    disabled={shiftsPage === totalShiftsPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};
